import React from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { FileText, Download, AlertCircle, Image as ImageIcon, FileSpreadsheet } from "lucide-react"
import { api, getToken } from "@/lib/api"
import { formatDateTime } from "@/lib/format"

export default function AttachmentPreviewModal({
  attachment,
  open,
  onOpenChange,
  token,
}) {
  if (!attachment) return null

  const authToken = token || localStorage.getItem("token") || ""
  const previewUrl = `${api.defaults.baseURL}/attachments/${attachment.id}/preview?token=${authToken}`
  const downloadUrl = `${api.defaults.baseURL}/attachments/${attachment.id}/download?token=${authToken}`

  const filename = attachment.original_filename || "Dosya"
  const mime = (attachment.mime_type || "").toLowerCase()
  const ext = filename.split(".").pop()?.toLowerCase() || ""

  const isPdf = mime === "application/pdf" || ext === "pdf"
  const isImage =
    mime.startsWith("image/") || ["png", "jpg", "jpeg", "webp"].includes(ext)
  const isOfficeOrOther = !isPdf && !isImage

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return "-"
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] flex flex-col p-6 bg-white border border-[#DCE2E8] shadow-xl">
        <DialogHeader className="shrink-0 pb-3 border-b border-[#DCE2E8]">
          <div className="flex items-center gap-2">
            {isPdf && <FileText className="h-5 w-5 text-rose-500 shrink-0" />}
            {isImage && <ImageIcon className="h-5 w-5 text-blue-500 shrink-0" />}
            {isOfficeOrOther && <FileSpreadsheet className="h-5 w-5 text-amber-500 shrink-0" />}
            <DialogTitle className="text-base font-semibold text-[#171A1F] truncate" title={filename}>
              {filename}
            </DialogTitle>
          </div>
          <DialogDescription className="text-xs text-slate-500 flex items-center gap-3 pt-1">
            <span>Boyut: {formatFileSize(attachment.file_size)}</span>
            {attachment.created_at && (
              <>
                <span>•</span>
                <span>Yüklenme: {formatDateTime(attachment.created_at)}</span>
              </>
            )}
            {attachment.mime_type && (
              <>
                <span>•</span>
                <span className="font-mono">{attachment.mime_type}</span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto py-4 min-h-[350px] flex items-center justify-center">
          {isPdf ? (
            <div className="w-full h-[65vh] rounded-md overflow-hidden border border-[#DCE2E8] bg-slate-50">
              <iframe
                src={previewUrl}
                title={filename}
                className="w-full h-full border-0"
              />
            </div>
          ) : isImage ? (
            <div className="w-full flex items-center justify-center p-4 bg-slate-100/70 rounded-md border border-[#DCE2E8] min-h-[350px] max-h-[65vh] overflow-auto">
              <img
                src={previewUrl}
                alt={filename}
                className="max-h-[60vh] max-w-full object-contain rounded shadow-sm"
              />
            </div>
          ) : (
            <div className="w-full max-w-md p-6 bg-slate-50 rounded-lg border border-[#DCE2E8] text-center space-y-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto">
                <AlertCircle className="w-6 h-6" />
              </div>
              <div className="space-y-1">
                <h4 className="font-semibold text-sm text-[#171A1F]">
                  Bu dosya türü için uygulama içi önizleme desteklenmiyor.
                </h4>
                <p className="text-xs text-slate-500">
                  {ext.toUpperCase()} formatındaki dosyaları görüntülemek için cihazınıza indirebilirsiniz.
                </p>
              </div>
              <div className="bg-white rounded border border-[#E2E8F0] p-3 text-left text-xs space-y-1.5 text-slate-600">
                <div className="flex justify-between">
                  <span className="text-slate-400">Dosya Adı:</span>
                  <span className="font-medium text-[#171A1F] truncate max-w-[200px]" title={filename}>{filename}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Dosya Türü:</span>
                  <span className="font-mono uppercase">{ext || "Bilinmiyor"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Boyut:</span>
                  <span>{formatFileSize(attachment.file_size)}</span>
                </div>
                {attachment.created_at && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Yüklenme:</span>
                    <span>{formatDateTime(attachment.created_at)}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 pt-3 border-t border-[#DCE2E8] flex sm:justify-between items-center gap-2">
          <div className="text-xs text-slate-400 hidden sm:block">
            {isOfficeOrOther ? "İndirerek açabilirsiniz." : "Önizleme görünümü"}
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            <Button
              asChild
              className="bg-[#168DEB] hover:bg-[#1272BE] text-white shadow-sm"
              data-testid="attachment-preview-download-btn"
            >
              <a href={downloadUrl} target="_blank" rel="noreferrer" download={filename}>
                <Download className="mr-2 h-4 w-4" />
                İndir
              </a>
            </Button>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-[#DCE2E8] text-slate-700 hover:bg-slate-50"
            >
              Kapat
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
